/**
 * Columnas de stock por condición/calidad (alineado con kardex y bulk API).
 */
export const STOCK_QUALITY_COLUMNS = [
  { key: 'good', label: 'Bueno' },
  { key: 'damaged', label: 'Dañado' },
  { key: 'defective', label: 'Defectuoso' },
  { key: 'reusable', label: 'Reutilizable' },
  { key: 'non_reusable', label: 'No reutilizable' },
];

export function mergeStockByCondition(apiRow) {
  const z = (v) => {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : 0;
  };
  const src = apiRow?.stock_by_condition || {};
  return {
    good: z(src.good),
    damaged: z(src.damaged),
    defective: z(src.defective),
    reusable: z(src.reusable),
    non_reusable: z(src.non_reusable),
  };
}

export function formatStockQty(q) {
  const n = parseFloat(q);
  if (!Number.isFinite(n)) return '0';
  if (Math.abs(n - Math.round(n)) < 0.0001) return String(Math.round(n));
  return n.toLocaleString('es-PE', { maximumFractionDigits: 3 });
}

export function formatAverageCost(q) {
  const n = parseFloat(q);
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 4 });
}

const qtyToInputString = (v) => {
  const n = parseFloat(v);
  const x = Number.isFinite(n) ? n : 0;
  return Math.abs(x - Math.round(x)) < 0.0001 ? String(Math.round(x)) : String(x);
};

/**
 * Valores iniciales de conteo físico a partir del stock por calidad (strings para inputs).
 * Si el desglose suma ~0 pero hay stock total, se asigna a Bueno (datos históricos sin condición).
 */
export function countedStringsFromStockByCondition(stockByCondition, totalStockHint = undefined) {
  const o = {};
  for (const { key } of STOCK_QUALITY_COLUMNS) {
    o[key] = qtyToInputString(stockByCondition?.[key] ?? 0);
  }
  let sum = STOCK_QUALITY_COLUMNS.reduce((s, { key }) => s + (parseFloat(o[key]) || 0), 0);
  const t =
    totalStockHint !== undefined && totalStockHint !== null
      ? parseFloat(totalStockHint)
      : NaN;
  if (Number.isFinite(t) && Math.abs(t) > 0.0001 && Math.abs(sum) < 0.0001) {
    o.good = qtyToInputString(t);
  }
  return o;
}

export function emptyCountedByConditionStrings() {
  return Object.fromEntries(STOCK_QUALITY_COLUMNS.map(({ key }) => [key, '']));
}

export function totalFromCountedByCondition(cbc) {
  return STOCK_QUALITY_COLUMNS.reduce((s, { key }) => {
    const n = parseFloat(cbc?.[key]);
    return s + (Number.isFinite(n) ? n : 0);
  }, 0);
}

/** Conteo físico en modo simple: todo en Bueno, resto en cero. */
export function countedSimpleToByCondition(totalQty) {
  const n = parseFloat(totalQty);
  const val = Number.isFinite(n) ? n : 0;
  return Object.fromEntries(
    STOCK_QUALITY_COLUMNS.map(({ key }) => [
      key,
      key === 'good' ? qtyToInputString(val) : '0',
    ])
  );
}

export function hasQualityAdjustmentDiff(row, epsilon = 0.0001) {
  if (row?.stockAtEntry === null || row?.stockAtEntry === undefined) return false;
  if (!row.stockByCondition || !row.countedByCondition) return false;
  return STOCK_QUALITY_COLUMNS.some(({ key }) => {
    const counted = parseFloat(row.countedByCondition[key]);
    const stock = parseFloat(row.stockByCondition[key]);
    return Math.abs((Number.isFinite(counted) ? counted : 0) - (Number.isFinite(stock) ? stock : 0)) > epsilon;
  });
}

export function hasSimpleAdjustmentDiff(row, epsilon = 0.0001) {
  if (row?.stockAtEntry === null || row?.stockAtEntry === undefined) return false;
  if (!row.countedByCondition) return false;
  const total = totalFromCountedByCondition(row.countedByCondition);
  const ref = parseFloat(row.stockAtEntry);
  return Math.abs(total - (Number.isFinite(ref) ? ref : 0)) > epsilon;
}

export function resolveCostOnlyTargetCostManual(existing, apiRow, stockZero, costStr, adjustmentMode) {
  if (adjustmentMode !== 'cost_only') {
    return existing?.targetCostManual ?? null;
  }
  if (stockZero) {
    return null;
  }
  const prev = existing?.targetCostManual;
  if (prev === null || prev === undefined || String(prev).trim() === '') {
    return costStr;
  }
  const prevCost = parseFloat(existing?.averageCost);
  const prevTarget = parseFloat(prev);
  if (
    Number.isFinite(prevCost) &&
    Number.isFinite(prevTarget) &&
    Math.abs(prevTarget - prevCost) <= 0.0001
  ) {
    return costStr;
  }
  return String(prev);
}

export function isMissingCatalogProduct(row) {
  if (row?.productMissing === true) return true;
  const name = String(row?.name ?? '');
  return /\(no encontrado\)/i.test(name) || /^Producto #\d+$/i.test(name.trim());
}

export function hasCostAdjustmentDiff(row, epsilon = 0.0001) {
  if (isMissingCatalogProduct(row)) return false;
  if (row?.stockAtEntry === null || row?.stockAtEntry === undefined) return false;
  const stock = parseFloat(row.stockAtEntry);
  if (!Number.isFinite(stock) || Math.abs(stock) < epsilon) return false;
  const current = parseFloat(row.averageCost);
  const targetRaw = row.targetCostManual;
  if (targetRaw === null || targetRaw === undefined || String(targetRaw).trim() === '') return false;
  const target = parseFloat(targetRaw);
  if (!Number.isFinite(target)) return false;
  const base = Number.isFinite(current) ? current : 0;
  return Math.abs(target - base) > epsilon;
}

export function formatInventoryValue(qty, unitCost) {
  const q = parseFloat(qty);
  const c = parseFloat(unitCost);
  if (!Number.isFinite(q) || !Number.isFinite(c)) return '—';
  return (q * c).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** Costo unitario que se usaría al guardar la línea de ajuste. */
export function resolveAdjustmentRowUnitCost(row) {
  const n = (v) => {
    const x = parseFloat(v);
    return Number.isFinite(x) ? x : 0;
  };
  if (row?.stockAtEntry === null || row?.stockAtEntry === undefined) return 0;
  const stockZero = Math.abs(n(row.stockAtEntry)) < 0.0001;
  return stockZero ? n(row.averageCostManual) : n(row.averageCost);
}

/** Filas con costo promedio negativo (inventario/kardex inconsistente). */
export function findRowsWithNegativeAdjustmentCost(rows, adjustmentMode = 'simple') {
  if (adjustmentMode === 'cost_only') return [];
  return (rows || []).filter((row) => resolveAdjustmentRowUnitCost(row) < -0.0001);
}

export function formatNegativeCostProductsMessage(rows, maxNames = 8) {
  const list = rows || [];
  if (list.length === 0) return '';
  const labels = list
    .slice(0, maxNames)
    .map((r) => r.name || r.sku || `Producto #${r.product_id}`);
  const extra = list.length > maxNames ? ` y ${list.length - maxNames} más` : '';
  return `${labels.join(', ')}${extra}`;
}

/** Filas con diferencia real para enviar al backend. */
export function buildAdjustmentSubmitRows(rows, adjustmentMode = 'simple') {
  return (rows || [])
    .filter((row) => {
      if (isMissingCatalogProduct(row)) return false;
      const productId = Number(row.product_id);
      if (!Number.isFinite(productId) || productId <= 0) return false;
      if (row.stockAtEntry === null || row.stockAtEntry === undefined) return false;
      if (adjustmentMode === 'cost_only') {
        return hasCostAdjustmentDiff(row);
      }
      if (!row.countedByCondition) return false;
      if (adjustmentMode === 'quality') {
        return hasQualityAdjustmentDiff(row);
      }
      return hasSimpleAdjustmentDiff(row);
    })
    .map((row) => buildAdjustmentSubmitItem(row, adjustmentMode));
}

export function formatAdjustmentSummaryMessage(response) {
  if (!response) return 'Guía registrada';
  if (response.message) return response.message;
  const summary = response.adjustment_summary;
  if (!summary) return 'Guía registrada';
  const skipped = summary.skipped?.length || 0;
  return `Guía registrada (${summary.items_applied} aplicados, ${skipped} omitidos).`;
}

export function adjustmentSummaryToastType(response) {
  const summary = response?.adjustment_summary;
  if (!summary?.skipped?.length) return 'success';
  const notable = summary.skipped.some((s) =>
    ['costo_negativo', 'sin_stock'].includes(s.reason)
  );
  return notable ? 'warning' : 'success';
}

/** Ítem de guía de ajuste para POST/PUT (incluye stock de referencia al cargar). */
export function buildAdjustmentSubmitItem(row, adjustmentMode) {
  const n = (v) => {
    const x = parseFloat(v);
    return Number.isFinite(x) ? x : 0;
  };
  if (adjustmentMode === 'cost_only') {
    return {
      product_id: row.product_id,
      unit_price: n(row.targetCostManual ?? row.averageCost),
    };
  }
  const unitPrice = resolveAdjustmentRowUnitCost(row);

  if (adjustmentMode === 'simple') {
    return {
      product_id: row.product_id,
      counted_quantity: totalFromCountedByCondition(row.countedByCondition),
      unit_of_measure: row.unit_of_measure,
      unit_price: unitPrice,
      system_stock_at_entry: n(row.stockAtEntry),
    };
  }

  const counted_by_condition = normalizeCountedForSubmit(row, true);
  const item = {
    product_id: row.product_id,
    counted_by_condition,
    unit_of_measure: row.unit_of_measure,
    unit_price: unitPrice,
  };
  if (row.stockByCondition) {
    item.system_stock_by_condition = Object.fromEntries(
      STOCK_QUALITY_COLUMNS.map(({ key }) => [key, n(row.stockByCondition[key])])
    );
  }
  return item;
}

export function normalizeCountedForSubmit(row, countByQuality) {
  if (countByQuality) {
    return Object.fromEntries(
      STOCK_QUALITY_COLUMNS.map(({ key }) => {
        const n = parseFloat(row.countedByCondition?.[key]);
        return [key, Number.isFinite(n) ? n : 0];
      })
    );
  }
  const total = totalFromCountedByCondition(row.countedByCondition);
  return Object.fromEntries(
    STOCK_QUALITY_COLUMNS.map(({ key }) => [key, key === 'good' ? total : 0])
  );
}

export function convertRowsCountMode(rows) {
  return rows.map((r) => {
    if (!r.countedByCondition) return r;
    const total = totalFromCountedByCondition(r.countedByCondition);
    return { ...r, countedByCondition: countedSimpleToByCondition(total) };
  });
}

const parseQty = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

/**
 * Construye o actualiza filas de guía de ajuste desde la respuesta bulk de stock/costo.
 * Si prev está vacío (formulario con lazy load de productos), crea todas las filas.
 */
export function mergeAdjustmentRowsFromBulk(prev, bulkData, { adjustmentMode = 'simple' } = {}) {
  const data = bulkData || {};
  const isQuality = adjustmentMode === 'quality';

  const patchRow = (productId, apiRow, existing) => {
    if (!Number.isFinite(productId) || productId <= 0) {
      return null;
    }
    const stock = apiRow ? parseQty(apiRow.total_stock) : 0;
    const stockZero = Math.abs(stock) < 0.0001;
    const cost = stockZero ? 0 : apiRow ? parseQty(apiRow.average_cost) : 0;
    const sbc = apiRow ? mergeStockByCondition(apiRow) : null;
    const costStr = stockZero ? '0' : String(cost);
    const countedByCondition = sbc
      ? isQuality
        ? countedStringsFromStockByCondition(sbc, stock)
        : countedSimpleToByCondition(stock)
      : existing?.countedByCondition ?? null;

    const productMissing =
      apiRow?.product_exists === false ||
      (existing?.productMissing === true && apiRow?.product_exists !== true);

    return {
      product_id: productId,
      sku: existing?.sku || apiRow?.sku || '',
      name:
        existing?.name ||
        apiRow?.name ||
        (apiRow?.sku ? String(apiRow.sku) : '') ||
        `Producto #${productId}`,
      unit_of_measure: existing?.unit_of_measure || apiRow?.unit_of_measure || 'UN',
      productMissing,
      stockAtEntry: stock,
      stockByCondition: sbc,
      averageCost: cost,
      averageCostManual: stockZero ? '0' : existing?.averageCostManual ?? null,
      targetCostManual: resolveCostOnlyTargetCostManual(
        existing,
        apiRow,
        stockZero,
        costStr,
        adjustmentMode
      ),
      countedByCondition,
    };
  };

  if (!prev || prev.length === 0) {
    return Object.keys(data)
      .map((key) => patchRow(parseInt(key, 10), data[key], null))
      .filter(Boolean)
      .sort((a, b) => (a.name || '').localeCompare(b.name || '', 'es'));
  }

  const seen = new Set(prev.map((r) => r.product_id));
  const updated = prev
    .map((r) => {
      const apiRow = data[String(r.product_id)] || data[r.product_id];
      return patchRow(r.product_id, apiRow, r);
    })
    .filter(Boolean);

  for (const key of Object.keys(data)) {
    const productId = parseInt(key, 10);
    if (seen.has(productId)) continue;
    const row = patchRow(productId, data[key], null);
    if (row) updated.push(row);
  }

  return updated.sort((a, b) => (a.name || '').localeCompare(b.name || '', 'es'));
}
